import "./index.css";
import logo15min from "./assets/15min-logo.png";
import bnsLogo from "./assets/bns-logo.png";
import m1RadioLogo from "./assets/m1-radio-logo.png";
import galagoLogo from "./assets/galago-logo.png";
import galagoScreenshot from "./assets/galago-screenshot.png";
import jolitaPranckeviciene from "./assets/jolita-pranckeviciene.png";
import eugestaLogoDark from "./assets/eugesta-logo-dark.svg";
import nomadLogo from "./assets/nomad-logo.png";
import coccLogo from "./assets/cocc-logo.png";
import communityElevenLabsImg from "./assets/community-elevenlabs-hackathon.png";
import communityCursorCafeImg from "./assets/community-cursor-cafe-vilnius.png";
import teamAndrzejChmura from "./assets/team-andrzej-chmura-new.png";
import teamTomasDirvonskas from "./assets/team-tomas-dirvonskas.png";
import teamEdvinasBartkus from "./assets/team-edvinas-bartkus-new.png";
import teamViktorasLaukevicius from "./assets/team-viktoras-laukevicius-new.png";
import teamArmandasVaicikauskas from "./assets/team-armandas-vaicikauskas.png";
import stickerElevenLabs from "./assets/sticker-elevenlabs.png";
import stickerCursor from "./assets/sticker-cursor.png";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { capturePostHog } from "./posthog";

function useScrollY() {
  const [state, setState] = useState({ y: 0, maxY: 0 });
  useEffect(() => {
    const update = () =>
      setState({
        y: window.scrollY,
        maxY:
          document.documentElement.scrollHeight - window.innerHeight,
      });
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  return state;
}


const NAV_SECTIONS = [
  { id: "in-one-sentence", label: "In one sentence", glyph: "①", glyphHover: "❶" },
  { id: "what-we-do", label: "What we do", glyph: "②", glyphHover: "❷" },
  {
    id: "what-we-shipped",
    label: "What we shipped",
    glyph: "③",
    glyphHover: "❸",
  },
  { id: "our-first-spin-out", label: "Our first spin-out", glyph: "④", glyphHover: "❹" },
  { id: "who-we-are", label: "Who we are", glyph: "⑤", glyphHover: "❺" },
  { id: "contact", label: "We want you to call", glyph: "⑥", glyphHover: "❻" },
] as const;


function ExternalLinkArrow({ className = "" }: { className?: string }) {
  return (
    <span
      className={`normal-case ms-0.5 inline-block shrink-0 ${className}`.trim()}
      aria-hidden
    >
      ↗
    </span>
  );
}

/** Chevron for accordion open/close state. */
function AccordionUpDownMark({
  open,
  variant,
}: {
  open: boolean;
  variant: "on-dark" | "on-light";
}) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`size-4 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""} ${variant === "on-dark" ? "text-white/45" : "text-black/45"}`}
      aria-hidden
    >
      <path d="M3 6l5 5 5-5" />
    </svg>
  );
}

const WHO_WE_ARE_TRAITS: readonly { glyph: string; text: string }[] = [
  { glyph: "→", text: "Open, no fluff, value-focused." },
  { glyph: "→", text: "Engineers become generalists." },
  { glyph: "→", text: "Product people ship code." },
];

const TEAM: readonly {
  name: string;
  surname: string;
  roles: readonly string[];
  /** Avatar placeholder until photos ship. */
  initials: string;
  /** Optional headshot; initials used when omitted. */
  photo?: string;
  linkedin?: string;
}[] = [
  {
    name: "Tomas",
    surname: "Dirvonskas",
    roles: ["Co-founder"],
    initials: "TD",
    photo: teamTomasDirvonskas,
    linkedin: "https://www.linkedin.com/in/tdirvonskas/",
  },
  {
    name: "Edvinas",
    surname: "Bartkus",
    roles: ["Co-founder"],
    initials: "EB",
    photo: teamEdvinasBartkus,
    linkedin: "https://www.linkedin.com/in/edvinasbartkus/",
  },
  {
    name: "Viktoras",
    surname: "Laukevičius",
    roles: ["Founding engineer"],
    initials: "VL",
    photo: teamViktorasLaukevicius,
    linkedin: "https://www.linkedin.com/in/viktoras-laukevi%C4%8Dius-72864363/",
  },
  {
    name: "Andrzej",
    surname: "Chmura",
    roles: ["Founding engineer"],
    initials: "AC",
    photo: teamAndrzejChmura,
    linkedin: "https://www.linkedin.com/in/achmura/",
  },
  {
    name: "Armandas",
    surname: "Vaičikauskas",
    roles: ["Design Engineer"],
    initials: "AV",
    photo: teamArmandasVaicikauskas,
    linkedin: "https://www.linkedin.com/in/efemeralizacija/",
  },
];


const WHAT_WE_DO_MODES: readonly {
  title: string;
  body: ReactNode;
}[] = [
  {
    title: "Build products with you",
    body: (
      <>
        From concept to launch. Senior engineers orchestrating AI agents
        alongside them, so you ship faster and sharper than competitors still
        figuring out where AI fits.
      </>
    ),
  },
  {
    title: "Build AI tools for your team",
    body: (
      <>
        We find what&apos;s eating your team&apos;s time and take it off their
        plate. Sales co-pilots, editorial assistants, reporting that runs
        itself, agents wired into your internal systems.
      </>
    ),
  },
  {
    title: "Build our own products",
    body: (
      <>
        When a problem we keep solving for clients turns out to belong to a
        whole industry, we spin it out.{" "}
        <a
          href="#our-first-spin-out"
          className="underline decoration-1 underline-offset-2 hover:opacity-70"
        >
          See section&nbsp;3.
        </a>
      </>
    ),
  },
];

const WHAT_WE_DO_CLOSING_NOTE =
  "Not sure where to start? We'll map it out. No slides — practical outputs only.";

type ShippedLogoKind =
  | "bns"
  | "15min"
  | "saltz"
  | "eugesta"
  | "galago"
  | "nomad";

/** Project type (shown as pills). */
type ShippedWorkTag =
  | "Product"
  | "Tool"
  | "Advisory";

const SHIPPED_BY_COMPANY: readonly {
  id: string;
  company: string;
  sector: string;
  logo?: ShippedLogoKind;
  items: readonly {
    id: string;
    title: string;
    tags: readonly ShippedWorkTag[];
    body: ReactNode;
  }[];
}[] = [
  {
    id: "15min",
    company: "15min",
    sector: "Editorial tools that save ~30% of the team's time",
    logo: "15min",
    items: [
      {
        id: "15min-automation",
        title: "Editorial automation",
        tags: ["Tool"],
        body: (
          <>
            Adds metadata to stories with one click — keywords, tags, headlines,
            social posts, related articles. Saves the editorial team nearly 30%
            of their time. More capacity to go deeper on their coverage.
          </>
        ),
      },
      {
        id: "easy-read",
        title: "Easy-to-read tool",
        tags: ["Tool"],
        body: (
          <>
            Turns any article into easy-to-read format based on EU guidelines.
            Editors don&apos;t have to rewrite manually. More accessible
            content, published faster.
          </>
        ),
      },
      {
        id: "media-monitoring",
        title: "Media monitoring",
        tags: ["Tool"],
        body: (
          <>
            Detects brands buying sponsored content across national media and
            turns them into sales leads in real time. Gives editorial teams
            visibility into topic coverage and paywall strategy across
            competitors.
          </>
        ),
      },
    ],
  },
  {
    id: "bns",
    company: "BNS",
    sector: "Editorial tools and daily sprint automation",
    logo: "bns",
    items: [
      {
        id: "bns-editorial",
        title: "Editorial tools",
        tags: ["Tool"],
        body: (
          <>
            Helps editors scan overnight news and draft stories for one-click
            publishing. Generates daily newsletters from editor-curated stories.
            Saves hours per week during the busiest parts of the news cycle.
          </>
        ),
      },
    ],
  },
  {
    id: "galago",
    company: "Galago / M-1",
    sector: "Radio intelligence — spun out as Galago",
    logo: "galago",
    items: [
      {
        id: "galago-platform",
        title: "Radio intelligence platform",
        tags: ["Product"],
        body: (
          <>
            Built for M-1 Radio Group. Real-time ad monitoring, topic tracking,
            playlist and competitor insights — all from live radio streams.
            Valuable enough that we spun it into a standalone product.{" "}
            <a
              href="https://galago.fm"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                capturePostHog("shipped_galago_link_clicked", {
                  destination: "https://galago.fm",
                })
              }
              className="inline-flex items-baseline hover:opacity-80"
            >
              <span className="underline decoration-1 underline-offset-2">
                galago.fm
              </span>
              <ExternalLinkArrow />
            </a>
          </>
        ),
      },
    ],
  },
  {
    id: "nomad",
    company: "Nomad Group",
    sector: "Office lease platform, co-built from scratch",
    logo: "nomad",
    items: [
      {
        id: "nomad-platform",
        title: "Lease management platform",
        tags: ["Product"],
        body: (
          <>
            Full office lease management platform for NYC landlords. Co-building
            from scratch.
          </>
        ),
      },
    ],
  },
  {
    id: "eugesta",
    company: "Eugesta",
    sector: "AI roadmap driving their transformation",
    logo: "eugesta",
    items: [
      {
        id: "eugesta-roadmap",
        title: "AI roadmap",
        tags: ["Advisory"],
        body: (
          <>
            Designed an AI roadmap with the Eugesta team in a short, intensive
            sprint. Now used internally to drive their AI-first transformation.
            In partnership with Civitta.
          </>
        ),
      },
    ],
  },
  {
    id: "saltz",
    company: "Saltz",
    sector: "Price automation, handed over, running internally",
    logo: "saltz",
    items: [
      {
        id: "saltz-prices",
        title: "Price automation",
        tags: ["Tool"],
        body: (
          <>
            Automated product price extraction from PDFs and spreadsheets. Built
            it, handed it over — Saltz runs it internally now. One of those
            teams where non-tech people build their own tools.
          </>
        ),
      },
    ],
  },
];

function CoccPressButton({
  className,
  variant = "yellow",
  label = "Reach out",
}: {
  className?: string;
  variant?: "yellow" | "blue" | "mono";
  label?: ReactNode;
}) {
  const [pressed, setPressed] = useState(false);
  const accentBg =
    variant === "blue"
      ? "bg-[#709CD2]"
      : variant === "mono"
        ? "bg-white"
        : "bg-yellow";

  const isMono = variant === "mono";
  const shellBg = isMono ? "bg-white" : "bg-black";
  const faceBg = isMono ? "bg-black" : "bg-white";
  const textColor = isMono ? "text-black" : "text-black";

  return (
    <div
      className={`flex rounded-sm ${shellBg} p-[3px] select-none ${className ?? ""}`}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
    >
      <div
        className={`relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg ${faceBg}`}
      >
        <div
          className={`pointer-events-none absolute ${accentBg} transition-[top,left,right,bottom,border-radius,box-shadow] duration-150 ease-out ${
            pressed
              ? "inset-0 rounded-lg"
              : "bottom-0 left-[3px] right-0 top-[3px] rounded-md"
          }`}
          style={{
            boxShadow: pressed
              ? `inset 0 0 28px rgba(${isMono ? "255,255,255" : "0,0,0"},0.12), inset 0 0 10px rgba(${isMono ? "255,255,255" : "0,0,0"},0.06)`
              : "none",
          }}
          aria-hidden
        />
        {label != null && (
          <span
            className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-2 text-center font-gt-mono text-[11px] md:text-[12px] font-medium uppercase tracking-wider transition-transform duration-150 ease-out ${textColor} ${
              pressed
                ? "-translate-x-px -translate-y-px"
                : "translate-x-0 translate-y-0"
            }`}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

/** Natural width ÷ height of `cocc-logo.png` (currently 990×229). */
const HEADER_LOGO_ASPECT = 990 / 229;
const LOGO_W_MAX = 288;
const LOGO_W_MIN = 200;
const LOGO_BOX_H = Math.round(LOGO_W_MAX / HEADER_LOGO_ASPECT);
const PAD_Y_MAX = 40;
const PAD_Y_MIN = 12;
const SHRINK_DISTANCE = 120;
const HEADER_INITIAL_BAR_H = LOGO_BOX_H + PAD_Y_MAX;

/** Scroll Y at or below this counts as “at top”: header button jumps to #contact; otherwise it scrolls to top first. */
const HERO_AT_TOP_SCROLL_Y = 40;

function prefersReducedMotionScrollBehavior(): ScrollBehavior {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";
}

function lerp(a: number, b: number, t: number) {
  return Math.round(a + (b - a) * t);
}

function PolaroidCard({
  src,
  alt,
  caption,
  detail,
  date,
  rotation = 0,
  sticker,
}: {
  src: string;
  alt: string;
  caption: string;
  detail: string;
  date?: string;
  rotation?: number;
  sticker?: { src: string; alt: string };
}) {
  return (
    <div
      className="relative w-64 shrink-0 bg-white p-2 pb-10 shadow-xl transition-transform duration-300 hover:scale-105 md:w-72"
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      <img
        src={src}
        alt={alt}
        className="aspect-4/3 w-full object-cover"
        loading="lazy"
        decoding="async"
        draggable={false}
      />
      {sticker && (
        <img
          src={sticker.src}
          alt={sticker.alt}
          className="pointer-events-none absolute -right-2 bottom-2 size-14 -rotate-6 rounded-lg drop-shadow-md md:size-16"
          draggable={false}
        />
      )}
      <div className="mt-3 px-1">
        <p
          className="text-xl text-black/80"
          style={{ fontFamily: "'Caveat', cursive" }}
        >
          {caption}
        </p>
        <p className="font-gt-mono text-[10px] uppercase tracking-wide text-black/40">
          {detail}{date && <span className="ml-2">· {date}</span>}
        </p>
      </div>
    </div>
  );
}

export function App() {
  const { y: scrollY, maxY: maxScrollY } = useScrollY();

  const whatWeDoScrollRef = useRef<HTMLDivElement>(null);
  const whatWeDoSectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const scroll = whatWeDoScrollRef.current;
    const section = whatWeDoSectionRef.current;
    if (!scroll || !section) return;

    const onWheel = (e: WheelEvent) => {
      const atStart = scroll.scrollLeft <= 1;
      const atEnd = scroll.scrollLeft >= scroll.scrollWidth - scroll.clientWidth - 1;

      if ((e.deltaY > 0 && atEnd) || (e.deltaY < 0 && atStart)) return;

      e.preventDefault();
      scroll.scrollLeft += e.deltaY;
    };

    section.addEventListener("wheel", onWheel, { passive: false });
    return () => section.removeEventListener("wheel", onWheel);
  }, []);

  const tEnter = Math.min(Math.max(scrollY / SHRINK_DISTANCE, 0), 1);
  const tLeave = Math.min(Math.max((maxScrollY - scrollY) / SHRINK_DISTANCE, 0), 1);
  const t = tEnter * tLeave;
  const logoW = lerp(LOGO_W_MAX, LOGO_W_MIN, t);
  const logoH = Math.round(logoW / HEADER_LOGO_ASPECT);
  const padY = lerp(PAD_Y_MAX, PAD_Y_MIN, t);
  const headerBarH = logoH + padY;
  const headerLinkScrollsToTop = scrollY > HERO_AT_TOP_SCROLL_Y;


  return (
    <div className="flex flex-col bg-background min-h-dvh">
      <div
        className="relative flex-1 bg-background md:max-w-screen-xl md:mx-auto md:border-l md:border-r md:border-black/10"
        style={{
          paddingBottom: `calc(${headerBarH}px + env(safe-area-inset-bottom, 0px))`,
        }}
      >
        <header
          className="fixed bottom-0 left-0 right-0 z-50 flex items-center px-8 md:px-16 lg:px-20 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-full md:max-w-screen-xl"
          style={{
            paddingBottom: `calc(${padY}px + env(safe-area-inset-bottom, 0px))`,
          }}
        >
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              capturePostHog("hero_button_clicked");
              const behavior = prefersReducedMotionScrollBehavior();
              if (window.scrollY > HERO_AT_TOP_SCROLL_Y) {
                window.scrollTo({ top: 0, behavior });
              } else {
                window.scrollTo({ top: document.body.scrollHeight, behavior });
              }
            }}
            aria-label={headerLinkScrollsToTop ? "Top" : "Reach out"}
            className="relative z-20 block shrink-0 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black"
            style={{ width: logoW, height: logoH }}
          >
            {/* Full logo (visible at top) */}
            <img
              src={coccLogo}
              alt=""
              className="pointer-events-none absolute inset-0 h-full w-full object-contain object-left"
              style={{ opacity: 1 - t }}
              draggable={false}
            />
            {/* CoccPressButton with arrow (visible when scrolled) */}
            <div
              className="absolute left-0 top-0 origin-top-left"
              style={{ opacity: t, width: LOGO_BOX_H, height: LOGO_BOX_H, transform: `scale(${logoH / LOGO_BOX_H})`, pointerEvents: t > 0.5 ? "auto" : "none" }}
            >
              <CoccPressButton className="size-full" label={null} />
              <span
                className="pointer-events-none absolute inset-0 flex items-center justify-center font-gt-expanded-black text-black"
                style={{ fontSize: LOGO_BOX_H * 0.4 }}
              >
                ↑
              </span>
            </div>
          </a>
        </header>

        <section
          className="flex h-dvh flex-col px-8 md:px-16 lg:px-20"
          style={{
            paddingBottom: `calc(${HEADER_INITIAL_BAR_H}px + env(safe-area-inset-bottom, 0px))`,
          }}
        >
          <nav
            aria-label="Sections"
            className="flex min-h-0 flex-1 flex-col items-start justify-center"
          >
            <ul className="flex flex-col gap-3 md:gap-4">
              {NAV_SECTIONS.map(({ id, label, glyph, glyphHover }) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      capturePostHog("hero_nav_clicked", {
                        label,
                        href: `#${id}`,
                      });
                      document.getElementById(id)?.scrollIntoView({
                        behavior: prefersReducedMotionScrollBehavior(),
                        block: "start",
                      });
                    }}
                    className="group inline-flex items-baseline gap-2 font-gt-expanded-black text-xl leading-none tracking-tight text-black transition-colors hover:text-yellow focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black md:gap-3 md:text-2xl lg:text-3xl"
                  >
                    <span
                      className="inline-grid shrink-0 place-items-center font-gt-expanded-black tabular-nums"
                      aria-hidden
                    >
                      <span className="col-start-1 row-start-1 opacity-100 transition-opacity duration-200 group-hover:opacity-0">
                        {glyph}
                      </span>
                      <span className="col-start-1 row-start-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        {glyphHover}
                      </span>
                    </span>
                    <span>{label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </section>

        {/* ── §1 In one sentence ── */}
        <section
          id="in-one-sentence"
          className="bg-black text-white px-8 py-20 md:px-16 md:py-32 lg:px-20 lg:py-40 flex flex-col gap-6 md:gap-8"
          aria-labelledby="in-one-sentence-heading"
        >
          <div className="flex flex-col gap-6 md:gap-8">
            <span
              className="font-gt-expanded-black text-yellow shrink-0"
              style={{ fontSize: 22 }}
              aria-hidden
            >
              ①
            </span>
            <h2
              id="in-one-sentence-heading"
              className="font-gt-expanded-medium leading-[1.05] tracking-tight text-white"
              style={{ fontSize: 28 }}
            >
              In one sentence
            </h2>
          </div>
          <h3
            id="hero-heading"
            className="font-gt-expanded-black text-[clamp(3rem,8vw,5.5rem)] leading-[0.95] tracking-tighter text-white"
          >
            AI-FIRST<br />PRODUCT<br />MAKERS
          </h3>
          <p className="mt-4 md:mt-6 max-w-lg font-gt-standard text-[17px] md:text-[19px] leading-relaxed text-white/60">
            Building for clients.<br className="lg:hidden" /> Building our own.
          </p>
        </section>

        {/* ── §2 What we do — three blocks + closing note ── */}
        <section
          ref={whatWeDoSectionRef}
          id="what-we-do"
          className="bg-yellow text-black px-8 py-16 md:px-16 md:py-24 lg:px-20 lg:py-32 flex flex-col gap-10 md:gap-14 overflow-x-clip"
          aria-labelledby="what-we-do-intro"
        >
          <div className="flex flex-col gap-6 md:gap-8">
            <span
              className="font-gt-expanded-black text-black shrink-0"
              style={{ fontSize: 22 }}
              aria-hidden
            >
              ②
            </span>
            <h2
              id="what-we-do-intro"
              className="font-gt-expanded-medium leading-[1.05] tracking-tight"
              style={{ fontSize: 28 }}
            >
              What we do
            </h2>
          </div>

          <div ref={whatWeDoScrollRef} className="-mx-8 md:-mx-16 lg:-mx-20 w-screen overflow-x-auto overflow-y-visible [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div
              className="flex flex-row gap-4 pb-2 snap-x snap-mandatory scroll-smooth md:gap-5 px-8 md:px-16 lg:px-20"
              role="list"
              aria-label="What we do"
            >
              {WHAT_WE_DO_MODES.map(({ title, body }) => (
                <article
                  key={title}
                  role="listitem"
                  className="relative flex w-[min(18rem,calc(100vw-6rem))] aspect-square shrink-0 snap-start flex-col gap-4 overflow-hidden rounded-md bg-yellow p-6 md:p-8"
                >
                  <RoundedFrameSvg className="pointer-events-none absolute inset-0 h-full w-full text-black" />
                  <div className="relative z-10 flex flex-col gap-4">
                    <h3
                      className="font-gt-expanded-medium leading-snug tracking-tight"
                      style={{ fontSize: 22 }}
                    >
                      {title}
                    </h3>
                    <p className="font-gt-standard text-[16px] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </article>
              ))}
              <div aria-hidden className="w-4 shrink-0 md:w-8 lg:w-10" />
            </div>
          </div>

          <div className="md:max-w-[904px]">
            <div className="flex flex-row items-center justify-between gap-6">
              <p
                className="font-gt-standard text-[16px] leading-relaxed text-black/80"
                role="note"
              >
                {WHAT_WE_DO_CLOSING_NOTE}
              </p>

              <a
                href="mailto:hello@comfortof.com"
                onClick={() =>
                  capturePostHog("contact_email_clicked", {
                    email: "hello@comfortof.com",
                  })
                }
                className="group inline-flex shrink-0 w-fit focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black"
              >
                <CoccPressButton className="size-20 md:size-24" variant="blue" label="Write to us" />
              </a>
            </div>
          </div>
        </section>

        <WhatWeShippedSection />

        {/* ── §3 Our first spin-out ── */}
        <section
          id="our-first-spin-out"
          className="bg-background text-black px-8 py-16 md:px-16 md:py-24 lg:px-20 lg:py-32"
        >
          <div className="flex flex-col gap-14 md:gap-16">

            {/* Full-width: glyph + heading */}
            <div className="flex flex-col gap-6">
              <span className="text-black font-gt-expanded-black" style={{ fontSize: 22 }} aria-hidden>④</span>
              <h2 className="font-gt-expanded-medium leading-[1.05] tracking-tight" style={{ fontSize: 28 }}>
                Our first spin-out
              </h2>
            </div>

            {/* Two columns: description + partnership (left) + screenshot (right) */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-x-16 md:items-start">
              {/* Left: description + partnership */}
              <div className="flex flex-col gap-6 md:gap-8">
                <p className="font-gt-standard text-[17px] md:text-[19px] leading-relaxed text-black/70">
                  Real-time ad monitoring, topic tracking, playlist and competitor
                  insights — all from live radio streams. Built inside a real
                  broadcaster until it was valuable enough to stand on its own.
                </p>

                {/* Partnership */}
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <img src={m1RadioLogo} alt="M-1 Radio" className="h-7 w-auto" />
                    <span className="font-gt-mono text-[11px] uppercase tracking-wide text-black/40">Partnership</span>
                  </div>
                  <p className="font-gt-standard text-[16px] md:text-[17px] leading-relaxed text-black">
                    Built alongside M-1 — Lithuania's largest commercial radio network.{" "}
                    <span className="text-black/50">Launched publicly at Radio Days Europe 2026.</span>
                  </p>
                  <a
                    href="https://galago.fm"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => capturePostHog("spin_out_galago_link_clicked", { destination: "https://galago.fm" })}
                    className="inline-flex items-baseline font-gt-mono text-[11px] md:text-[12px] uppercase tracking-wide text-black/40 hover:text-black transition-colors duration-200"
                  >
                    Visit galago.fm
                    <ExternalLinkArrow />
                  </a>
                </div>
              </div>

              {/* Right: screenshot */}

              <a
                href="https://galago.fm"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => capturePostHog("spin_out_galago_link_clicked", { destination: "https://galago.fm" })}
                className="rounded-sm bg-black p-[4px] block hover:opacity-90 transition-opacity duration-150 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
              >
                <div className="relative overflow-hidden rounded-md">
                  <img src={galagoScreenshot} alt="Galago — Stream Time analysis: M1 vs Power Hit Radio" className="w-full h-auto block" draggable={false} />
                  <div className="absolute inset-0 bg-yellow opacity-10 pointer-events-none" />
                </div>
              </a>
            </div>


          </div>
        </section>

        {/* ── §4 Who we are + community ── */}
        <section
          id="who-we-are"
          className="bg-black text-white px-8 py-16 md:px-16 md:py-24 lg:px-20 lg:py-32 flex flex-col justify-center gap-16 md:gap-24"
        >
          <div className="flex flex-col gap-10 md:gap-12">
            {/* Full-width: glyph + heading */}
            <div className="flex flex-col gap-6 md:gap-8">
              <span
                className="text-yellow font-gt-expanded-black"
                style={{ fontSize: 22 }}
                aria-hidden
              >
                ⑤
              </span>
              <h2
                className="font-gt-expanded-medium leading-[1.05] tracking-tight"
                style={{ fontSize: 28 }}
              >
                Who we are
              </h2>
            </div>

            {/* Two columns: desc+traits left, people right */}
            <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-x-16 md:gap-y-6 lg:gap-x-24 border-b border-white/15 pb-6 md:pb-8">
              {/* Left row 1: description + traits */}
              <div className="flex flex-col gap-8">
                <p className="font-gt-standard text-[17px] md:text-[19px] leading-relaxed text-white/85">
              Small team. Working together for years. Each of us has built
              digital products&nbsp;&mdash; some turned into unicorns. Now we
              channel all of that into AI.
                </p>

                <ul className="flex flex-col gap-3">
                  {WHO_WE_ARE_TRAITS.map(({ glyph, text }) => (
                    <li key={text} className="flex items-baseline gap-3">
                      <span
                        className="font-gt-expanded-black text-yellow tabular-nums shrink-0"
                        style={{ fontSize: 15 }}
                        aria-hidden
                      >
                        {glyph}
                      </span>
                      <span className="font-gt-standard text-[17px] md:text-[19px] leading-snug text-white" style={{ fontWeight: 500 }}>
                        {text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right: people grid — spans 2 rows on md+ */}
              <div className="flex flex-col gap-6 md:row-span-2">
                <ul className="m-0 grid list-none grid-cols-1 gap-5 p-0 lg:grid-cols-2 lg:gap-6">
                  {TEAM.map((member) => (
                    <TeamMemberCard key={`${member.name}-${member.surname}`} {...member} />
                  ))}
                </ul>
              </div>

              {/* Left row 2: join link — appears after team members on mobile */}
              <a
                href="mailto:join@comfortof.com"
                onClick={() => capturePostHog("contact_email_clicked", { email: "join@comfortof.com" })}
                className="inline-flex items-baseline font-gt-mono text-[11px] md:text-[12px] uppercase tracking-wide text-white/40 hover:text-yellow transition-colors duration-200"
              >
                Join our team&nbsp;&mdash;&nbsp;join@comfortof.com
                <ExternalLinkArrow />
              </a>
            </div>
          </div>

          <div className="flex w-full max-w-6xl flex-col gap-10 md:gap-12">
            <h3
              className="font-gt-expanded-medium leading-[1.05] tracking-tight text-white"
              style={{ fontSize: 28 }}
            >
              Community
            </h3>
            <p className="font-gt-standard text-[17px] md:text-[19px] leading-relaxed text-white/85 max-w-2xl">
              Active in the Vilnius tech scene since 2009. Started the local AI
              community in 2017.
            </p>
            <div className="-mx-8 md:-mx-16 lg:-mx-20 w-screen overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex flex-row gap-6 px-8 md:px-16 lg:px-20 py-8">
              <PolaroidCard
                src={communityCursorCafeImg}
                alt="Cursor Cafe Vilnius meetup"
                caption="Cursor Cafe Vilnius"
                detail="Organizer"
                date="Mar 2, 2026"
                rotation={3}
                sticker={{ src: stickerCursor, alt: "Cursor" }}
              />
              <PolaroidCard
                src={communityElevenLabsImg}
                alt="ElevenLabs hackathon — group photo"
                caption="ElevenLabs Hackathon"
                detail="Organizer"
                date="Dec 11, 2025"
                rotation={-2}
                sticker={{ src: stickerElevenLabs, alt: "ElevenLabs" }}
              />
            </div>
            </div>

            <ul className="flex flex-col border-t border-white/15">
              {[
                { label: "BMI Keynote", url: "https://www.notion.so/tdaiweekly/BMI-AI-business-models-2f4f68cbcd6c8081b7e6e28195bff834?source=copy_link", event: "BMI", date: "Jan 23, 2026" },
                { label: "BNS Keynote", url: "https://www.notion.so/tdaiweekly/BNS-AI-Bubble-326f68cbcd6c805a8b3ce524d2848c47?source=copy_link", event: "BNS", date: "Mar 18, 2026" },
              ].map(({ label, url, event, date }) => (
                <li key={event} className="flex items-baseline justify-between gap-4 border-b border-white/15 py-3">
                  <span className="flex items-baseline gap-2">
                    <span className="font-gt-expanded-medium text-[13px] md:text-[14px] text-white">{event}</span>
                    <span className="font-gt-mono text-[10px] md:text-[11px] uppercase tracking-wide text-white/40">Keynote</span>
                    <span className="font-gt-mono text-[10px] md:text-[11px] uppercase tracking-wide text-white/40">· {date}</span>
                  </span>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => capturePostHog("community_keynote_clicked", { event })}
                  className="inline-flex items-baseline gap-1 font-gt-expanded-medium text-[11px] md:text-[12px] text-white/40 hover:text-yellow transition-colors duration-200 shrink-0"
                >
                  View →
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── §5 Contact ── */}
        <section
          id="contact"
          className="bg-background text-black min-h-dvh flex flex-col px-8 md:px-16 lg:px-20"
        >
          <div className="flex-1 flex flex-col justify-center py-20 md:py-32">
            <span
              className="font-gt-expanded-black text-black mb-10 md:mb-14"
              style={{ fontSize: 22 }}
              aria-hidden
            >
              ⑥
            </span>

            <h2
              className="font-gt-expanded-medium leading-[0.92] tracking-tight"
              style={{ fontSize: "clamp(40px, 9vw, 120px)" }}
            >
              We want you to call.
            </h2>

            <p className="font-gt-standard text-[17px] md:text-[19px] leading-relaxed text-black/50 mt-4 md:mt-6">
              But email is fine too —{" "}
              <a
                href="mailto:hello@comfortof.com"
                onClick={() => capturePostHog("contact_email_clicked", { email: "hello@comfortof.com" })}
                className="text-black/50 hover:text-black transition-colors duration-200 underline underline-offset-2"
              >
                hello@comfortof.com
              </a>
            </p>

            <a
              href="mailto:hello@comfortof.com"
              onClick={() =>
                capturePostHog("contact_email_clicked", {
                  email: "hello@comfortof.com",
                })
              }
              className="mt-10 md:mt-16 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black inline-block w-fit"
            >
              <CoccPressButton className="size-28 md:size-32" label="Write to us" />
            </a>
          </div>

          <footer className="py-6 md:py-8 flex flex-col gap-2">
            <a
              href="https://www.youtube.com/watch?v=py4prb2kQ1o"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-baseline gap-0.5 font-gt-mono text-[10px] md:text-[11px] uppercase tracking-wide text-black/40 hover:text-black underline underline-offset-2 decoration-black/20 hover:decoration-black transition-colors duration-200"
            >
              that&apos;s it. get back to work.
              <ExternalLinkArrow />
            </a>
            <p className="font-gt-mono text-[10px] md:text-[11px] uppercase tracking-wide text-black/40">
              &copy; {new Date().getFullYear()} Comfort of Complex Computations
            </p>
          </footer>
        </section>
      </div>
    </div>
  );
}

function SaltzWordmark({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 60 30"
      className={className}
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M.191 20.536h.766c1.187 7.07 2.987 8.713 5.055 8.713 1.724 0 2.49-1.223 2.49-3.477 0-3.361-1.915-6.306-3.984-9.363C2.642 13.543 0 9.913 0 6.741c0-3.324 2.68-4.394 5.82-4.394 1.877 0 3.56.458 5.209.458l.919 9.019h-.804C9.994 4.678 8.538 3.11 6.472 3.11c-1.34 0-2.183.761-2.183 2.255 0 2.14 1.915 4.853 3.523 7.337 1.953 2.827 4.979 7.031 4.979 11.082 0 4.967-2.72 6.228-6.281 6.228-2.03 0-3.638-.458-5.515-.458l-.804-9.019ZM54.722 28.79c2.45 0 3.294-1.451 4.52-6.42h.762l-.957 7.184h-11.41v-.764l7.238-17.578h-1.608c-2.45 0-3.294 1.453-4.519 5.808h-.766l.957-6.572H59.93v.764L52.693 28.79h2.029ZM28.294 29.707h-.766c-.112-1.555-.445-2.78-.85-3.506-1.562 3.373-4.594 3.812-6.508 3.812-2.988 0-5.898-2.254-5.898-7.91 0-5.656 2.91-7.872 6.05-7.872 1.839 0 2.988.803 3.715 2.216 0-4.28-.536-5.693-2.606-5.693-1.532 0-3.255.76-4.863 3.4h-.766l1.187-3.706c1.226 0 3.332-.459 5.208-.459 3.907 0 6.127 1.873 6.127 6.343v8.98c.002 2.447-.03 4.395-.03 4.395Zm-4.259-7.604c0-4.853-1.072-6.723-2.566-6.723-1.493 0-2.45 2.178-2.45 6.723 0 4.544.957 6.764 2.45 6.764 1.494 0 2.568-1.91 2.568-6.764h-.002ZM35.024 26.08V2.347h-.382c-.995 1.107-1.99 1.223-3.368 1.223H29.24v.38c.434.119.751.232.98.43.042.036.08.075.116.117.34.399.436 1.096.436 2.549v22.511h5.783v-.38c-1.263-.348-1.531-.654-1.531-3.097ZM46.443 22.37c-.728 3.402-1.417 4.816-2.107 4.816-.765 0-.919-.802-.919-2.828v-10.28c0-.62.075-1.133.255-1.545.018-.04.037-.081.057-.12.422-.825 1.322-1.2 2.981-1.2v-.765h-3.293V4.983h-.382c-.799 2.775-2.106 4.485-4.564 5.174a7.011 7.011 0 0 1-1.6.291v.764c1.23 0 1.818.38 2.078 1.201l.035.12c.108.412.145.925.145 1.546v10.546c0 .323.01.632.03.928.19 2.86 1.289 4.46 3.304 4.46.842 0 1.511-.258 2.062-.74.18-.158.343-.333.488-.522a3.943 3.943 0 0 0 .221-.309c.048-.071.095-.145.14-.222.546-.918.954-2.157 1.335-3.642l.095-.376.046-.19c.11-.45.218-.92.326-1.41l-.733-.231Z"
      />
    </svg>
  );
}

const shippedLogoImg =
  "h-8 w-auto max-w-[64px] object-contain object-left sm:max-w-[80px]";

function ShippedClientLogo({ kind }: { kind?: ShippedLogoKind }) {
  if (kind === "bns") {
    return (
      <img src={bnsLogo} alt="" className={shippedLogoImg} draggable={false} />
    );
  }
  if (kind === "15min") {
    return (
      <img
        src={logo15min}
        alt=""
        className={shippedLogoImg}
        draggable={false}
      />
    );
  }
  if (kind === "saltz") {
    return (
      <SaltzWordmark className="h-7 w-auto max-w-[64px] text-black sm:max-w-[80px]" />
    );
  }
  if (kind === "eugesta") {
    return (
      <img
        src={eugestaLogoDark}
        alt=""
        className={shippedLogoImg}
        draggable={false}
      />
    );
  }
  if (kind === "galago") {
    return (
      <img
        src={m1RadioLogo}
        alt=""
        className="h-8 w-auto max-w-[64px] object-contain object-right sm:max-w-[80px]"
        draggable={false}
      />
    );
  }
  if (kind === "nomad") {
    return (
      <img
        src={nomadLogo}
        alt=""
        className={shippedLogoImg}
        draggable={false}
      />
    );
  }
  return (
    <span
      className="block h-8 w-14 rounded-sm border border-dashed border-black/15"
      aria-hidden
    />
  );
}

function ShippedDetails({
  open,
  items,
}: {
  open: boolean;
  items: (typeof SHIPPED_BY_COMPANY)[number]["items"];
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (open && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [open]);

  return (
    <div
      className="w-full overflow-hidden transition-[height,opacity] duration-300 ease-in-out"
      style={{ height, opacity: open ? 1 : 0 }}
    >
      <div ref={contentRef} className="pb-4 pt-4">
        <ul className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:gap-x-10 md:gap-y-7">
          {items.map((item) => (
            <li key={item.id}>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span
                    key={`${item.id}-${tag}`}
                    className="font-gt-mono rounded border border-black/20 bg-black/3 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-black/80"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h3 className="font-gt-expanded-medium mt-2 text-[16px] leading-snug text-black">
                {item.title}
              </h3>
              <p className="font-gt-standard mt-2 text-[15px] leading-relaxed text-black/90 md:text-[16px]">
                {item.body}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function WhatWeShippedSection() {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  return (
    <section
      id="what-we-shipped"
      className="bg-background text-foreground px-8 pb-10 pt-16 md:px-16 md:pb-14 md:pt-24 lg:px-20 lg:pt-28"
      aria-labelledby="what-we-shipped-heading"
    >
      <div>
        <span
          className="font-gt-expanded-black text-black"
          style={{ fontSize: 22 }}
          aria-hidden
        >
          ③
        </span>

        <h2
          id="what-we-shipped-heading"
          className="font-gt-expanded-medium leading-[1.05] tracking-tight text-black mt-6 md:mt-8"
          style={{ fontSize: 28 }}
        >
          What we shipped
        </h2>

        <div className="mt-8" role="list">
          {SHIPPED_BY_COMPANY.map(({ id, company, sector, logo, items }) => {
            const open = openIds.has(id);
            return (
              <div
                key={id}
                role="listitem"
                className="border-b border-black/20"
              >
                <button
                  type="button"
                  onClick={() =>
                    setOpenIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) {
                        next.delete(id);
                      } else {
                        next.add(id);
                        capturePostHog("shipped_company_opened", {
                          companyId: id,
                        });
                      }
                      return next;
                    })
                  }
                  aria-expanded={open}
                  aria-label={`${company}, ${sector}`}
                  className="flex w-full items-center gap-3 py-3.5 text-left text-black focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black sm:gap-4"
                >
                  <div className="flex h-10 w-18 shrink-0 items-center justify-start sm:h-11 sm:w-28">
                    <ShippedClientLogo kind={logo} />
                  </div>
                  <div className="min-w-0 flex-1">
                    {logo ? (
                      <p className="font-gt-standard text-[15px] leading-snug text-black/75 md:text-[16px]">
                        {sector}
                      </p>
                    ) : (
                      <p className="leading-snug">
                        <span className="font-gt-expanded-medium text-[16px] text-black md:text-[17px]">
                          {company}
                        </span>
                        <span className="font-gt-standard text-[15px] text-black/55 md:text-[16px]">
                          {" "}
                          · {sector}
                        </span>
                      </p>
                    )}
                  </div>
                  <AccordionUpDownMark open={open} variant="on-light" />
                </button>
                <ShippedDetails open={open} items={items} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function TeamMemberCard({
  name,
  surname,
  roles,
  initials,
  photo,
  linkedin,
}: {
  name: string;
  surname: string;
  roles: readonly string[];
  initials: string;
  photo?: string;
  linkedin?: string;
}) {
  const [pressed, setPressed] = useState(false);
  const linkedInProps = {
    href: linkedin,
    target: "_blank" as const,
    rel: "noopener noreferrer",
    onClick: () => capturePostHog("team_linkedin_clicked", { name: `${name} ${surname}` }),
  };
  return (
    <li className="flex min-w-0 items-center gap-2.5 md:gap-3">
      <a
        {...linkedInProps}
        className="flex size-10 shrink-0 select-none rounded-[4px] bg-white p-[2px] md:size-12 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
        onPointerDown={() => setPressed(true)}
        onPointerUp={() => setPressed(false)}
        onPointerLeave={() => setPressed(false)}
        onPointerCancel={() => setPressed(false)}
      >
        <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-[3px] bg-black">
          <div
            className={`absolute overflow-hidden transition-[inset,border-radius] duration-150 ease-out ${
              pressed ? "inset-0 rounded-[3px]" : "bottom-0 left-[2px] right-0 top-[2px] rounded-[3px]"
            } ${photo ? "" : "bg-yellow flex items-center justify-center font-gt-expanded-black text-[10px] tabular-nums text-black"}`}
          >
            {photo ? (
              <>
                <img src={photo} alt={`${name} ${surname}`} className="h-full w-full object-cover" draggable={false} />
                <div className="absolute inset-0 bg-yellow opacity-10 pointer-events-none" />
              </>
            ) : (
              initials
            )}
          </div>
        </div>
      </a>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="font-gt-expanded-medium text-[14px] text-white md:text-[15px]">
          {name} {surname}
        </span>
        <div className="flex items-center gap-1.5">
          {roles.map((roleLabel) => (
            <span
              key={roleLabel}
              className="inline-flex w-fit items-center rounded border border-white/20 bg-white/5 px-1.5 py-px font-gt-mono text-[9px] uppercase tracking-wider text-white/75 whitespace-nowrap md:text-[10px]"
            >
              {roleLabel}
            </span>
          ))}
          <a
            {...linkedInProps}
            className="text-white/40 hover:text-yellow transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
          >
            <svg viewBox="0 0 16 16" fill="currentColor" className="size-3.5 shrink-0" aria-label="LinkedIn">
              <path d="M0 1.146C0 .514.516 0 1.153 0h13.694C15.484 0 16 .514 16 1.146v13.708c0 .632-.516 1.146-1.153 1.146H1.153C.516 16 0 15.486 0 14.854zm4.943 12.248V6.169H2.542v7.225zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248S2.4 3.226 2.4 3.934c0 .694.521 1.248 1.327 1.248zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016l.016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225z" />
            </svg>
          </a>
        </div>
      </div>
    </li>
  );
}

/** Square with rounded corners — from `assets/rounded-frame.svg`; reuse on other pages. */
function RoundedFrameSvg({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 334 334"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      preserveAspectRatio="none"
      aria-hidden
    >
      <path
        d="M25.7121 0.930908H308.287C321.953 0.930908 333.069 12.0276 333.069 25.7123V308.288C333.069 321.954 321.972 333.069 308.287 333.069H25.7121C12.046 333.069 0.930664 321.972 0.930664 308.288V25.7123C0.930664 12.0276 12.0274 0.930908 25.7121 0.930908Z"
        stroke="currentColor"
        strokeWidth="1.86187"
        strokeMiterlimit={10}
      />
    </svg>
  );
}

function UpDownArrow({ className }: { className?: string }) {
  const clipId = useId().replace(/:/g, "");
  return (
    <svg
      viewBox="0 0 32 39"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className ?? "w-7 md:w-9 text-yellow"}
      aria-hidden
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M0 26.9521H5.18138V1.46769H12.0306V26.9521H17.2564L8.60598 38.4044L0 26.9521ZM19.9249 11.4524H14.7436L23.3495 0L32 11.4524H26.7741V36.6922H19.9249V11.4524Z"
          fill="currentColor"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="32" height="38.4044" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

export default App;
